import { Component, inject } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, AbstractControl } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { RichTextEditorComponent } from './../text-editor/rich-text-editor.component';
import { TranslateModule } from '@ngx-translate/core';
import { FileUploadComponent, FileUploadConfig } from '../file-upload/file-upload.component';
import { ExpandImageComponent } from '../expand-image/expand-imagecomponent';
import { PortalService, UtilsService } from '../../services';
import { finalize } from 'rxjs/operators';
import { SnackBar } from '../../enums';

@Component({
  selector: 'educare-external-speaker-dialog',
  standalone: true,
  templateUrl: './external-speaker-dialog.component.html',
  styleUrls: ['./external-speaker-dialog.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    RichTextEditorComponent,
    TranslateModule,
    FileUploadComponent
  ]
})
export class ExternalSpeakerDialogComponent {
  private readonly dialogRef = inject(MatDialogRef<ExternalSpeakerDialogComponent>);
  private readonly fb = inject(FormBuilder);
  private readonly dialog = inject(MatDialog);
  private readonly portalService = inject(PortalService);
  private readonly utils = inject(UtilsService);
  // File upload configurations
  fileUploadConfig: FileUploadConfig = {
    label: 'Profile Picture',
    placeholder: 'userInfo.placeholder.profilePic',
    required: false, // Optional field - user can skip uploading
    allowedTypes: ['image/jpeg', 'image/jpg', 'image/png'],
    maxSizeInBytes: 5 * 1024 * 1024, // 5MB
    showPreview: false,
    acceptAttribute: 'image/*',
    isRtl: false,
    skipDeleteConfirmation: true, // Skip confirmation dialog when deleting
    imageUrlParams: {},
    validationMessages: {
      required: 'eventForm.validations.image',
      invalidType: 'eventForm.validations.imageFormat',
      maxSizeExceeded: 'eventForm.validations.imageSize'
    }
  };

  selectedFile: File | null = null;
  imageUrl: string = '';
  externalForm: FormGroup = this.fb.group({
    fullName: ['', Validators.required],
    email: ['', [Validators.required, Validators.email]],
    designation: ['', Validators.required],
    profilePicture: [
      '',
      FileUploadComponent.fileValidator(() => !!this.imageUrl, this.fileUploadConfig.required || false)
    ],
    bio: ['', Validators.required]
  });
  submitted = false;

  createdUserId: any;

  get imageUrlControl(): AbstractControl {
    return this.externalForm.get('profilePicture')!;
  }

  onFileSelected(file: File | null): void {
    this.selectedFile = file;
    const control = this.externalForm.get('profilePicture');
    if (file) {
      this.submitted = false;
      const reader = new FileReader();
      reader.onload = () => {
        this.imageUrl = reader.result as string;
      };
      reader.readAsDataURL(file);
      control?.setValue(file);
    } else {
      this.imageUrl = '';
      control?.setValue(null);
    }
    // Ensure the control is marked as touched to trigger validation display
    control?.markAsTouched();
    control?.updateValueAndValidity();
  }

  onFileRemoved(): void {
    this.selectedFile = null;
    this.imageUrl = '';
    this.submitted = false;
    const control = this.externalForm.get('profilePicture');
    control?.setValue(null);
    control?.markAsTouched();
    control?.updateValueAndValidity();
  }

  openImageDialog(event: { url: string; isNewFile: boolean }): void {
    this.dialog.open(ExpandImageComponent, {
      data: {
        image: event.url,
        isServer: !event.isNewFile,
        query: event.isNewFile ? {} : { userId: this.createdUserId, isEvent: true }
      }
    });
  }

  onBioChanged(newValue: string): void {
    this.externalForm.get('bio')?.setValue(newValue);
    this.externalForm.get('bio')?.markAsTouched();
    this.externalForm.get('bio')?.updateValueAndValidity();
  }

  showError(controlName: string, errorType: string): boolean {
    const control = this.externalForm.get(controlName);
    return !!control && control.hasError(errorType) && (control.touched || control.dirty || this.submitted);
  }

  onSave(): void {
    this.submitted = true;
    //  If profile picture is optional, ignore its errors completely
    const pictureControl = this.externalForm.get('profilePicture');
    if (!this.fileUploadConfig.required) {
      pictureControl?.setErrors(null);
    }
    if (this.externalForm.invalid) {
      this.externalForm.markAllAsTouched();
      return;
    }
    const formData = this.buildFormData();
    const url = this.utils.getEncryptedUrls('ZXh0ZXJuYWwtc3BlYWtlcnM=');
    this.portalService
      .post(url, formData)
      .pipe(finalize(() => (this.submitted = false)))
      .subscribe({
        next: (res: any) => {
          // Optional: success toast

          this.utils.openSnackBar?.(
            this.utils.translation?.('EXTERNAL_SPEAKER_CREATED_SUCCESS') || 'Saved successfully',
            this.utils.translation?.('snackbar.cancel'),
            SnackBar.Success
          );
          this.dialogRef.close(res?.data);
        },
        error: (error: any) => {
          this.utils.openSnackBar?.(
            this.utils.toTitleCase?.(error?.error?.message || 'Unable to save'),
            this.utils.translation?.('snackbar.cancel'),
            SnackBar.Error
          );
        }
      });
  }

  private buildFormData(): FormData {
    const f = this.externalForm.value;
    const fd = new FormData();
    // Map control names -> API field names
    fd.append('name', (f.fullName || '').trim());
    fd.append('email', (f.email || '').trim());
    fd.append('designation', (f.designation || '').trim());
    fd.append('bio', f.bio || '');
    // Attach file if selected
    if (this.selectedFile) {
      fd.append('profile_photo', this.selectedFile, this.selectedFile.name);
    }

    return fd;
  }

  onClose(): void {
    this.dialogRef.close();
  }
}
